import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  InputGroup,
  Collapse 
} from 'react-bootstrap';

const BannerFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  loading 
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value,
      page: 1 // Reset página quando filtros mudarem
    };
    onFiltersChange(newFilters);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Busca já é feita automaticamente no onChange
  };

  const hasActiveFilters = () => {
    return (
      filters.search ||
      filters.is_active !== 'all' ||
      filters.created_by !== 'all' ||
      filters.ordering !== 'sort_order'
    );
  };

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Body>
        <Row className="align-items-end">
          {/* Busca */}
          <Col lg={4} md={6} className="mb-3">
            <Form onSubmit={handleSearchSubmit}>
              <Form.Label className="small fw-bold text-muted mb-1">
                Buscar
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Título, subtítulo ou texto do botão..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  disabled={loading}
                />
                <Button 
                  variant="outline-secondary" 
                  type="submit"
                  disabled={loading}
                >
                  <i className="las la-search" />
                </Button>
              </InputGroup>
            </Form>
          </Col>

          {/* Status */}
          <Col lg={2} md={3} className="mb-3">
            <Form.Label className="small fw-bold text-muted mb-1">
              Status
            </Form.Label>
            <Form.Select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              disabled={loading}
            >
              <option value="all">Todos</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </Form.Select>
          </Col>

          {/* Ordenação */}
          <Col lg={2} md={3} className="mb-3">
            <Form.Label className="small fw-bold text-muted mb-1">
              Ordenar por
            </Form.Label>
            <Form.Select
              value={filters.ordering}
              onChange={(e) => handleFilterChange('ordering', e.target.value)}
              disabled={loading}
            >
              <option value="sort_order">Ordem</option>
              <option value="-created_at">Mais Recentes</option>
              <option value="created_at">Mais Antigos</option>
              <option value="-view_count">Mais Visualizados</option>
              <option value="-click_count">Mais Clicados</option>
              <option value="title">Título (A-Z)</option>
              <option value="-title">Título (Z-A)</option>
            </Form.Select>
          </Col>

          {/* Ações */}
          <Col lg={4} className="mb-3">
            <div className="d-flex gap-2 justify-content-lg-end">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
                disabled={loading}
              >
                <i className={`las la-${showAdvanced ? 'minus' : 'plus'} me-1`} />
                Filtros Avançados
              </Button>
              
              {hasActiveFilters() && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={onClearFilters}
                  disabled={loading}
                >
                  <i className="las la-times me-1" />
                  Limpar
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Filtros Avançados */}
        <Collapse in={showAdvanced}>
          <div>
            <hr className="my-3" />
            <Row>
              {/* Criado por */}
              <Col lg={3} md={6} className="mb-3">
                <Form.Label className="small fw-bold text-muted mb-1">
                  Criado por
                </Form.Label>
                <Form.Select
                  value={filters.created_by}
                  onChange={(e) => handleFilterChange('created_by', e.target.value)}
                  disabled={loading}
                >
                  <option value="all">Todos os usuários</option>
                  {/* Aqui você pode adicionar opções dinâmicas de usuários */}
                </Form.Select>
              </Col>

              {/* Itens por página */}
              <Col lg={2} md={3} className="mb-3">
                <Form.Label className="small fw-bold text-muted mb-1">
                  Por página
                </Form.Label>
                <Form.Select
                  value={filters.page_size}
                  onChange={(e) => handleFilterChange('page_size', parseInt(e.target.value))}
                  disabled={loading}
                >
                  <option value={12}>12 itens</option>
                  <option value={24}>24 itens</option>
                  <option value={48}>48 itens</option>
                  <option value={96}>96 itens</option>
                </Form.Select>
              </Col>

              {/* Placeholder para futuras expansões */}
              <Col lg={7} className="mb-3">
                <div className="text-muted small pt-4">
                  <i className="las la-info-circle me-1" />
                  Use os filtros para encontrar banners específicos
                </div>
              </Col>
            </Row>
          </div>
        </Collapse>

        {/* Indicadores de filtros ativos */}
        {hasActiveFilters() && (
          <>
            <hr className="my-3" />
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <span className="small text-muted fw-bold">Filtros ativos:</span>
              
              {filters.search && (
                <span className="badge bg-primary">
                  Busca: "{filters.search}"
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('search', '')}
                  />
                </span>
              )}
              
              {filters.is_active !== 'all' && (
                <span className="badge bg-success">
                  Status: {filters.is_active === 'true' ? 'Ativos' : 'Inativos'}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('is_active', 'all')}
                  />
                </span>
              )}
              
              {filters.created_by !== 'all' && (
                <span className="badge bg-info">
                  Criado por: {filters.created_by}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('created_by', 'all')}
                  />
                </span>
              )}
              
              {filters.ordering !== 'sort_order' && (
                <span className="badge bg-warning text-dark">
                  Ordenação customizada
                  <button
                    type="button"
                    className="btn-close ms-2"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('ordering', 'sort_order')}
                  />
                </span>
              )}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default BannerFilters;